import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";

import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

const StyledModal = styled(Dialog, {
  shouldForwardProp: isPropValid,
})<{ $customMaxWidth?: string }>`
  .MuiDialog-paper {
    width: 90%;
    max-width: ${(props) => props.$customMaxWidth || "550px"};
    border-radius: ${Radius.M};
    padding: ${(props) => props.theme.spacing(Spacing.M)};
    background-color: ${(props) => props.theme.colors.gray};
    box-shadow: none;
    border: 1px solid ${(props) => props.theme.colors.border};
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .MuiDialogTitle-root {
    padding: 0;
    padding-bottom: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(5)};
  }

  .MuiDialogContent-root {
    padding: ${(props) => props.theme.spacing(2, 0)};
  }

  .MuiDialogActions-root {
    padding: 0;
    padding-top: ${(props) => props.theme.spacing(2)};

    & > * {
      margin: 0;
      margin-right: ${(props) => props.theme.spacing(1.5)};

      &:last-child {
        margin-right: 0;
      }
    }
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${(props) => props.theme.spacing(4)};
  right: ${(props) => props.theme.spacing(4)};
  color: ${(props) => props.theme.palette.common.white};
  padding: ${(props) => props.theme.spacing(0.5)};

  &:hover {
    background-color: ${(props) => props.theme.palette.action.hover};
  }
`;

interface BlModalProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  onCancel: () => void;
  onConfirm?: () => void;
  cancelButtonText?: string;
  confirmButtonText?: string;
  isLoading?: boolean;
  maxWidth?: string;
}

export const BlModal = ({
  isOpen,
  title,
  children,
  onCancel,
  onConfirm,
  cancelButtonText = "Cancel",
  confirmButtonText = "Confirm",
  isLoading = false,
  maxWidth,
}: BlModalProps): React.ReactNode => {
  return (
    <StyledModal
      open={isOpen}
      onClose={onCancel}
      fullWidth
      $customMaxWidth={maxWidth}
      sx={{
        scrollbarGutter: "stable both-edges",
      }}
    >
      <CloseButton onClick={onCancel} aria-label="close">
        <CloseIcon />
      </CloseButton>

      <DialogTitle>
        <Typography
          mb={Spacing.M}
          sx={{
            fontSize: "24px",
            fontWeight: 600,
            color: (theme) => theme.palette.common.white,
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          mx: -2,
          flex: 1,
          overflowY: "auto",
          scrollbarGutter: "stable both-edges",
        }}
      >
        <Box px={1}>{children}</Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1.5 }}>
        <Button variant="outlined" onClick={onCancel}>
          {cancelButtonText}
        </Button>
        {onConfirm && (
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </StyledModal>
  );
};
